import { NativeEvent } from '~/shared/NativeBridge';

interface InboundMessage {
  type: string;
  payload: unknown;
}

type InboundHandler<P> = (payload: P) => void;

type AnyInboundHandler = InboundHandler<unknown>;

// Mapa global pra sobreviver a remounts do composable.
const inboundHandlers = new Map<string, Set<AnyInboundHandler>>();
let inboundListenerInstalled = false;

function parseInbound(raw: unknown): InboundMessage | null {
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof (parsed as InboundMessage).type === 'string'
    ) {
      return parsed as InboundMessage;
    }
  } catch {
    // mensagem que não é nossa, ignora
  }

  return null;
}

function installInboundListener(): void {
  if (inboundListenerInstalled || typeof window === 'undefined') {
    return;
  }
  inboundListenerInstalled = true;

  // Aceita origin vazia (WebView nativa via injectJavaScript) e
  // mesma origin. Bloqueia iframes de terceiros (gateways, KYC, jogos)
  // pra impedir que forjem mensagens da biometria.
  function isOriginAllowed(origin: string): boolean {
    if (origin === '' || origin === 'null') return true;
    try {
      return origin === window.location.origin;
    } catch {
      return false;
    }
  }

  const handle = (raw: unknown): void => {
    const message = parseInbound(raw);

    if (!message) {
      return;
    }

    const handlers = inboundHandlers.get(message.type);

    if (!handlers) {
      return;
    }

    handlers.forEach((fn) => {
      try {
        fn(message.payload);
      } catch {
        // erro no handler nao deve quebrar outros handlers
      }
    });
  };

  // So `window.message`. Versoes recentes do RN WebView usam esse canal;
  // ouvir `document.message` em paralelo causava entrega duplicada.
  window.addEventListener('message', (e: MessageEvent) => {
    if (!isOriginAllowed(e.origin)) return;
    handle(e.data);
  });
}

/**
 * Composable para falar com o app nativo via WebView.
 *
 * Uso (web -> app):
 *   const { dispatch } = useNativeBridge();
 *   dispatch(new AppsflyerEvent('signup', { method: 'email' }));
 *
 * Uso (app -> web):
 *   useNativeBridge().on<{ version: string }>('appReady', ({ version }) => ...);
 */
export const useNativeBridge = () => {
  function isNativeApp(): boolean {
    return NativeEvent.isNativeApp();
  }

  function dispatch<T extends string, P>(event: NativeEvent<T, P>): boolean {
    return event.dispatch();
  }

  function on<P>(type: string, handler: InboundHandler<P>): () => void {
    installInboundListener();

    let set = inboundHandlers.get(type);

    if (!set) {
      set = new Set();
      inboundHandlers.set(type, set);
    }

    const wrapped = handler as AnyInboundHandler;

    set.add(wrapped);

    return () => {
      const current = inboundHandlers.get(type);

      if (!current) {
        return;
      }
      current.delete(wrapped);
      if (current.size === 0) {
        inboundHandlers.delete(type);
      }
    };
  }

  return {
    isNativeApp,
    dispatch,
    on,
  };
};
